import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { errorToast } from "@telluscompany/design-system";

import { useLocalization } from "@i18n";
import {
  axiosDeleteData,
  axiosPatchData,
  axiosPostData,
} from "@shared/api/clients";

import {
  INSPECTION_QUERY_KEY,
  inspectionStructureEndpoint,
} from "./inspection.queries";
import type {
  CreateInspectionRequest,
  InspectionAttachment,
  InspectionDetail,
  InspectionFinalizeResponse,
  InspectionListItem,
  InspectionParticipant,
  InspectionParticipantsPatchRequest,
  InspectionSignatureRequest,
  SaveInspectionRequest,
} from "./inspection.types";

const inspectionEndpoint = (structureId: string, inspectionId: string) =>
  `${inspectionStructureEndpoint(structureId)}/${inspectionId}`;

/**
 * Mutations de escrita usam `errorToast` com `error.parsedMessage` (já traz o
 * código `X.Y.Z` obrigatório — error-mapping.mdc + i18n.mdc §5.2), no mesmo
 * padrão de Admin/Managerial.
 *
 * Escopos agregados que dependem de escrita: listagem, KPI/faixa EC e gráfico.
 * O detalhe (Y=5) fica de fora de propósito — Y=6 devolve o detalhe; Y=11
 * devolve só `participants[]` (endpoints §12.2). O POST de criação (Y=4)
 * devolve só o item da listagem (endpoints §5.2). Invalidar o detalhe faria a
 * ficha aberta voltar ao estado anterior enquanto o refetch está em voo.
 */
const INVALIDATED_ON_WRITE = ["list", "summary", "chart"] as const;

const useInvalidateInspections = () => {
  const queryClient = useQueryClient();

  return () =>
    Promise.all(
      INVALIDATED_ON_WRITE.map((scope) =>
        queryClient.invalidateQueries({
          queryKey: [INSPECTION_QUERY_KEY, scope],
          exact: false,
        }),
      ),
    );
};

type InspectionMutationFallback =
  | "common.errorCreating"
  | "common.errorEditing"
  | "common.errorDeleting";

const useInspectionMutationErrorToast = () => {
  const { translate } = useLocalization();

  return (error: AxiosError, fallbackKey: InspectionMutationFallback) => {
    errorToast({
      description: error.parsedMessage ?? translate(fallbackKey),
    });
  };
};

/** Y=4 — POST /v1/inspection/structures/:structureId — 201 = item da listagem. */
export const createInspection = (
  structureId: string,
  payload: CreateInspectionRequest,
) =>
  axiosPostData<InspectionListItem, CreateInspectionRequest>(
    inspectionStructureEndpoint(structureId),
    payload,
  );

export const useCreateInspectionMutation = (structureId: string) => {
  const invalidate = useInvalidateInspections();
  const notifyError = useInspectionMutationErrorToast();

  return useMutation<InspectionListItem, AxiosError, CreateInspectionRequest>({
    mutationFn: (payload) => createInspection(structureId, payload),
    onSuccess: () => invalidate(),
    onError: (error) => notifyError(error, "common.errorCreating"),
  });
};

/** Y=6 — PATCH /v1/inspection/structures/:structureId/:inspectionId */
export const saveInspection = (
  structureId: string,
  inspectionId: string,
  payload: SaveInspectionRequest,
) =>
  axiosPatchData<InspectionDetail, SaveInspectionRequest>(
    inspectionEndpoint(structureId, inspectionId),
    payload,
  );

export const useSaveInspectionMutation = (
  structureId: string,
  inspectionId: string,
) => {
  const invalidate = useInvalidateInspections();
  const notifyError = useInspectionMutationErrorToast();

  return useMutation<InspectionDetail, AxiosError, SaveInspectionRequest>({
    mutationFn: (payload) => saveInspection(structureId, inspectionId, payload),
    onSuccess: () => invalidate(),
    onError: (error) => notifyError(error, "common.errorEditing"),
  });
};

/**
 * Y=7 gera o PDF no mesmo request (I4 — Chromium até 60s + S3). 180s cobre
 * launch + render + upload sem alterar o timeout das demais rotas (I8).
 */
export const FINALIZE_INSPECTION_TIMEOUT_MS = 180_000;

/**
 * Y=7 — POST /v1/inspection/structures/:structureId/:inspectionId/finalize
 *
 * Devolve o resumo do commit (endpoints §8.1) — `pdfVerificationCode` só vem
 * preenchido porque o PDF é gerado **antes** de `finalized` (I2/I3).
 */
export const finalizeInspection = (structureId: string, inspectionId: string) =>
  axiosPostData<InspectionFinalizeResponse>(
    `${inspectionEndpoint(structureId, inspectionId)}/finalize`,
    {},
    { timeout: FINALIZE_INSPECTION_TIMEOUT_MS },
  );

export const useFinalizeInspectionMutation = (
  structureId: string,
  inspectionId: string,
) => {
  const invalidate = useInvalidateInspections();
  const notifyError = useInspectionMutationErrorToast();

  return useMutation<InspectionFinalizeResponse, AxiosError, void>({
    mutationFn: () => finalizeInspection(structureId, inspectionId),
    onSuccess: () => invalidate(),
    onError: (error) => notifyError(error, "common.errorEditing"),
  });
};

/** Y=8 — DELETE /v1/inspection/structures/:structureId/:inspectionId (soft delete) */
export const deleteInspection = (structureId: string, inspectionId: string) =>
  axiosDeleteData<void>(inspectionEndpoint(structureId, inspectionId));

export const useDeleteInspectionMutation = (structureId: string) => {
  const invalidate = useInvalidateInspections();
  const notifyError = useInspectionMutationErrorToast();

  return useMutation<void, AxiosError, string>({
    mutationFn: (inspectionId) => deleteInspection(structureId, inspectionId),
    onSuccess: () => invalidate(),
    onError: (error) => notifyError(error, "common.errorDeleting"),
  });
};

/** Y=10 — POST /v1/inspection/.../items/:itemKey/photos */
export interface UploadInspectionPhotoVariables {
  itemKey: string;
  occurrenceKey?: string;
  file: File;
}

export const uploadInspectionPhoto = (
  structureId: string,
  inspectionId: string,
  { itemKey, occurrenceKey, file }: UploadInspectionPhotoVariables,
) => {
  const formData = new FormData();
  formData.append("file", file);

  const query = occurrenceKey
    ? `?occurrenceKey=${encodeURIComponent(occurrenceKey)}`
    : "";

  return axiosPostData<InspectionAttachment, FormData>(
    `${inspectionEndpoint(structureId, inspectionId)}/items/${encodeURIComponent(
      itemKey,
    )}/photos${query}`,
    formData,
  );
};

export const useUploadInspectionPhotoMutation = (
  structureId: string,
  inspectionId: string,
) => {
  const invalidate = useInvalidateInspections();
  const notifyError = useInspectionMutationErrorToast();

  return useMutation<
    InspectionAttachment,
    AxiosError,
    UploadInspectionPhotoVariables
  >({
    mutationFn: (variables) =>
      uploadInspectionPhoto(structureId, inspectionId, variables),
    onSuccess: () => invalidate(),
    onError: (error) => notifyError(error, "common.errorCreating"),
  });
};

/** Y=11 — PATCH /v1/inspection/.../participants */
export const patchInspectionParticipants = (
  structureId: string,
  inspectionId: string,
  payload: InspectionParticipantsPatchRequest,
) =>
  axiosPatchData<InspectionParticipant[], InspectionParticipantsPatchRequest>(
    `${inspectionEndpoint(structureId, inspectionId)}/participants`,
    payload,
  );

export const usePatchInspectionParticipantsMutation = (
  structureId: string,
  inspectionId: string,
) => {
  const invalidate = useInvalidateInspections();
  const notifyError = useInspectionMutationErrorToast();

  return useMutation<
    InspectionParticipant[],
    AxiosError,
    InspectionParticipantsPatchRequest
  >({
    mutationFn: (payload) =>
      patchInspectionParticipants(structureId, inspectionId, payload),
    onSuccess: () => invalidate(),
    onError: (error) => notifyError(error, "common.errorEditing"),
  });
};

/** Y=12 — POST /v1/inspection/.../participants/:participantId/signature */
export interface CreateInspectionSignatureVariables
  extends InspectionSignatureRequest {
  participantId: string;
}

export const createInspectionSignature = (
  structureId: string,
  inspectionId: string,
  { participantId, ...payload }: CreateInspectionSignatureVariables,
) =>
  axiosPostData<InspectionParticipantSignatureResponse, InspectionSignatureRequest>(
    `${inspectionEndpoint(
      structureId,
      inspectionId,
    )}/participants/${participantId}/signature`,
    payload,
  );

export interface InspectionParticipantSignatureResponse {
  id: string;
  signedName: string;
  signatureFontKey: string;
  signedAt: string;
}

export const useCreateInspectionSignatureMutation = (
  structureId: string,
  inspectionId: string,
) => {
  const invalidate = useInvalidateInspections();
  const notifyError = useInspectionMutationErrorToast();

  return useMutation<
    InspectionParticipantSignatureResponse,
    AxiosError,
    CreateInspectionSignatureVariables
  >({
    mutationFn: (variables) =>
      createInspectionSignature(structureId, inspectionId, variables),
    onSuccess: () => invalidate(),
    onError: (error) => notifyError(error, "common.errorCreating"),
  });
};
